/**
 * 请求数据采集器
 * 处理请求数据的创建和过滤
 */

import type {
  RequestData,
  RequestFilter,
  PendingRequestData,
  ResponseInfo,
} from '../types/network'

/**
 * 创建请求数据记录
 */
export function createRequestData(
  pending: PendingRequestData,
  response: ResponseInfo
): RequestData {
  return {
    id: pending.id,
    url: pending.url,
    method: pending.method,
    type: pending.type,
    requestHeaders: pending.requestHeaders,
    requestBody: pending.requestBody,
    status: response.status,
    statusText: response.statusText,
    responseHeaders: response.responseHeaders,
    responseBody: response.responseBody,
    requestStatus: response.requestStatus,
    errorMessage: response.errorMessage,
    startTime: pending.startTime,
    endTime: response.endTime,
    duration: response.endTime - pending.startTime,
    pageUrl: pending.pageUrl,
  }
}

/**
 * 过滤请求列表
 */
export function filterRequests(
  requests: RequestData[],
  filter: RequestFilter
): RequestData[] {
  return requests.filter(request => {
    // 按请求方法过滤
    if (filter.method) {
      const methods = Array.isArray(filter.method) ? filter.method : [filter.method]
      if (!methods.includes(request.method)) {
        return false
      }
    }

    // 按请求状态过滤
    if (filter.requestStatus) {
      const statuses = Array.isArray(filter.requestStatus)
        ? filter.requestStatus
        : [filter.requestStatus]
      if (!statuses.includes(request.requestStatus)) {
        return false
      }
    }

    // 按状态码范围过滤
    if (filter.statusCodeRange && request.status !== undefined) {
      const [min, max] = filter.statusCodeRange
      if (request.status < min || request.status > max) {
        return false
      }
    }

    // 按耗时过滤
    if (filter.minDuration !== undefined && request.duration !== undefined) {
      if (request.duration < filter.minDuration) {
        return false
      }
    }
    if (filter.maxDuration !== undefined && request.duration !== undefined) {
      if (request.duration > filter.maxDuration) {
        return false
      }
    }

    // 按 URL 正则过滤
    if (filter.urlPattern) {
      if (!filter.urlPattern.test(request.url)) {
        return false
      }
    }

    // 自定义过滤函数
    if (filter.custom) {
      return filter.custom(request)
    }

    return true
  })
}

/**
 * 获取请求摘要信息
 */
export function getRequestSummary(requests: RequestData[]): {
  total: number
  success: number
  error: number
  pending: number
  avgDuration: number
  maxDuration: number
} {
  if (requests.length === 0) {
    return {
      total: 0,
      success: 0,
      error: 0,
      pending: 0,
      avgDuration: 0,
      maxDuration: 0,
    }
  }

  let success = 0
  let error = 0
  let pending = 0
  let totalDuration = 0
  let maxDuration = 0

  for (const request of requests) {
    switch (request.requestStatus) {
      case 'success':
        success++
        break
      case 'error':
      case 'timeout':
      case 'abort':
        error++
        break
      case 'pending':
        pending++
        break
    }

    if (request.duration !== undefined) {
      totalDuration += request.duration
      if (request.duration > maxDuration) {
        maxDuration = request.duration
      }
    }
  }

  return {
    total: requests.length,
    success,
    error,
    pending,
    avgDuration: totalDuration / requests.length,
    maxDuration,
  }
}
